/**
 * Gen2 callables on europe-west1 often answer the *first* request of a cold
 * start with HTTP 401 / UNAVAILABLE before the container is ready. The Firebase
 * client surfaces that as `functions/unauthenticated` even when the ID token
 * is valid — measured on `recordUserConsent` during onboarding (#597).
 *
 * One retry after a short pause is enough once the instance has started; a
 * genuine missing-auth failure fails again immediately and still surfaces.
 */

const TRANSIENT_SUFFIXES = [
  'deadline-exceeded',
  'unavailable',
  'unauthenticated',
] as const;

export function isTransientCallableError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const code = (err as { code?: unknown }).code;
  if (typeof code !== 'string' || code === '') return false;
  return TRANSIENT_SUFFIXES.some((s) => code.endsWith(s));
}

/** Run `fn`; on a transient callable error, wait briefly and run once more. */
export async function withTransientCallableRetry<T>(
  fn: () => Promise<T>,
  pauseMs = 400,
): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (!isTransientCallableError(err)) throw err;
    await new Promise<void>((resolve) => setTimeout(resolve, pauseMs));
    return await fn();
  }
}
